import { Image as ImageIcon, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Textarea } from "../../../components/ui/textarea";
import {
  sq,
  PDF_TEMPLATE_BODY_MAX,
  PDF_TEMPLATE_ACCEPT,
  formatFileSizeBytes,
  createEmptyPdfImageSlot,
  revokePdfImageObjectUrl,
  validatePdfTemplateImageFile,
  PDF_TEMPLATE_BODY_MIN_HEIGHT,
  resizePdfBodyTextarea,
} from "./quotationUtils";
import { SectionShell } from "./QuotationComponents";


function PdfTemplateImageField({
  label,
  hint,
  slot,
  error,
  editable,
  disabled,
  onSelectFile,
  onRemove,
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const hasFile = Boolean(slot?.previewUrl || slot?.fileName);

  function handleFiles(fileList) {
    const file = fileList?.[0] || null;
    if (!file) return;
    onSelectFile?.(file);
  }

  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#96a7c2]">{label}</p>
        {hint ? <p className="text-[11px] text-[#8ea0bd]">{hint}</p> : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={PDF_TEMPLATE_ACCEPT}
        className="sr-only"
        disabled={!editable || disabled}
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = "";
        }}
      />
      {hasFile ? (
        <div className="flex items-center gap-2.5 rounded-[10px] border border-[#d1d9e8] bg-[#fafcff] px-2.5 py-2">
          <div className="grid h-11 w-14 shrink-0 place-items-center overflow-hidden rounded-[7px] border border-[#dde5f1] bg-white">
            {slot.previewUrl ? (
              <img
                src={slot.previewUrl}
                alt={slot.fileName || label}
                className="h-full w-full object-contain"
              />
            ) : (
              <ImageIcon className="h-4 w-4 text-[#2944a8]" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-semibold text-[#2d394c]">
              {slot.fileName || sq("pdfTemplateImageUntitled")}
            </p>
            {slot.fileSize != null ? (
              <p className="mt-0.5 text-[11px] text-[#8ea0bd]">{formatFileSizeBytes(slot.fileSize)}</p>
            ) : null}
          </div>
          {editable ? (
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                disabled={disabled}
                onClick={() => inputRef.current?.click()}
                className="inline-flex h-7 items-center rounded-[7px] border border-[#d0d9ea] bg-white px-2.5 text-[11px] font-semibold text-[#2944a8] transition hover:border-[#9eb3dc] hover:bg-[#f7faff] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sq("browse")}
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={onRemove}
                aria-label={sq("removeFile")}
                className="inline-flex h-7 w-7 items-center justify-center rounded-[7px] border border-[#d0d9ea] bg-white text-[#6f82a1] transition hover:border-[#e4a5a0] hover:bg-[#fff6f5] hover:text-[#c74b40] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <div
          role={editable ? "button" : undefined}
          tabIndex={editable && !disabled ? 0 : undefined}
          onClick={() => {
            if (!editable || disabled) return;
            inputRef.current?.click();
          }}
          onKeyDown={(event) => {
            if (!editable || disabled) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragEnter={(event) => {
            if (!editable || disabled) return;
            event.preventDefault();
            setDragging(true);
          }}
          onDragOver={(event) => {
            if (!editable || disabled) return;
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={(event) => {
            if (!editable || disabled) return;
            event.preventDefault();
            setDragging(false);
          }}
          onDrop={(event) => {
            if (!editable || disabled) return;
            event.preventDefault();
            setDragging(false);
            handleFiles(event.dataTransfer?.files);
          }}
          className={`flex items-center gap-2.5 rounded-[10px] border border-dashed bg-[#fafcff] px-2.5 py-2.5 transition ${
            editable && !disabled ? "cursor-pointer hover:border-[#95addd] hover:bg-[#f5f8fd]" : "cursor-default opacity-90"
          } ${error ? "border-[#e4a5a0]" : dragging ? "border-[#95addd] bg-[#f5f8fd]" : "border-[#d1d9e8]"}`}
        >
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[8px] border border-[#dde5f1] bg-white text-[#2944a8]">
            <ImageIcon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-semibold text-[#2d394c]">{sq("pdfTemplateDropHint")}</p>
            <p className="mt-0.5 truncate text-[11px] text-[#8ea0bd]">{sq("pdfTemplateImageHint")}</p>
          </div>
          {editable ? (
            <button
              type="button"
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                inputRef.current?.click();
              }}
              className="inline-flex h-7 shrink-0 items-center rounded-[7px] border border-[#d0d9ea] bg-white px-2.5 text-[11px] font-semibold text-[#2944a8] transition hover:border-[#9eb3dc] hover:bg-[#f7faff] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sq("browse")}
            </button>
          ) : null}
        </div>
      )}
      {error ? <p className="mt-1 text-[11px] font-medium text-[#c74b40]">{error}</p> : null}
    </div>
  );
}

function PdfTemplateTab({
  headerImage,
  footerImage,
  bodyContent,
  setHeaderImage,
  setFooterImage,
  setBodyContent,
  errors = {},
  setErrors,
  editable = false,
  saving = false,
  loading = false,
  loadError = "",
}) {
  const bodyTextareaRef = useRef(null);

  useEffect(() => {
    resizePdfBodyTextarea(bodyTextareaRef.current);
  }, [bodyContent, loading]);

  function applyImageFile(kind, file) {
    const validationError = validatePdfTemplateImageFile(file);
    if (validationError) {
      setErrors?.((current) => ({ ...current, [kind]: validationError }));
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const setter = kind === "headerImage" ? setHeaderImage : setFooterImage;
    setter?.((current) => {
      revokePdfImageObjectUrl(current);
      return {
        file,
        previewUrl: objectUrl,
        fileName: file.name || `${kind}.png`,
        fileSize: Number(file.size) || null,
        existing: false,
        removed: false,
        objectUrl,
        retainedPayload: "",
      };
    });
    setErrors?.((current) => ({ ...current, [kind]: "" }));
  }

  function clearImage(kind) {
    const setter = kind === "headerImage" ? setHeaderImage : setFooterImage;
    setter?.((current) => {
      revokePdfImageObjectUrl(current);
      return {
        ...createEmptyPdfImageSlot(),
        removed: Boolean(current?.existing || current?.file),
      };
    });
    setErrors?.((current) => ({ ...current, [kind]: "" }));
  }

  if (loading) {
    return (
      <div className="rounded-[14px] border border-[#d8e0ed] bg-white px-5 py-8 text-center text-[13px] text-[#6f82a1]">
        {sq("pdfTemplateLoading")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {loadError ? (
        <div className="rounded-[12px] border border-[#f4cfcb] bg-[#fff6f5] px-4 py-3 text-[12px] text-[#b04d43]">
          {loadError}
        </div>
      ) : null}
      <SectionShell title={sq("pdfTemplate")} compact>
        <div className="space-y-4 px-4 py-3.5 sm:px-5">
          <div className="grid gap-3 sm:grid-cols-2 sm:gap-x-4">
            <PdfTemplateImageField
              label={sq("pdfTemplateHeaderImage")}
              hint={sq("pdfTemplateHeaderHint")}
              slot={headerImage}
              error={errors.headerImage}
              editable={editable}
              disabled={saving}
              onSelectFile={(file) => applyImageFile("headerImage", file)}
              onRemove={() => clearImage("headerImage")}
            />
            <PdfTemplateImageField
              label={sq("pdfTemplateFooterImage")}
              hint={sq("pdfTemplateFooterHint")}
              slot={footerImage}
              error={errors.footerImage}
              editable={editable}
              disabled={saving}
              onSelectFile={(file) => applyImageFile("footerImage", file)}
              onRemove={() => clearImage("footerImage")}
            />
          </div>
          <div>
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#96a7c2]">
                {sq("pdfTemplateBodyContent")}
              </p>
              <p
                className={`text-[11px] font-medium ${
                  String(bodyContent || "").length >= PDF_TEMPLATE_BODY_MAX
                    ? "text-[#c74b40]"
                    : "text-[#8ea0bd]"
                }`}
              >
                {String(bodyContent || "").length} / {PDF_TEMPLATE_BODY_MAX}
              </p>
            </div>
            <Textarea
              ref={bodyTextareaRef}
              value={bodyContent || ""}
              disabled={!editable || saving}
              maxLength={PDF_TEMPLATE_BODY_MAX}
              rows={3}
              placeholder={sq("pdfTemplateBodyPlaceholder")}
              onChange={(event) => {
                const next = String(event.target.value || "").slice(0, PDF_TEMPLATE_BODY_MAX);
                setBodyContent?.(next);
                setErrors?.((current) => ({ ...current, bodyContent: "" }));
                resizePdfBodyTextarea(event.currentTarget);
              }}
              onInput={(event) => resizePdfBodyTextarea(event.currentTarget)}
              className="min-h-[72px] resize-none overflow-hidden rounded-[10px] border-[#d1d9e8] bg-white text-[13px] leading-5 text-[#2d394c] shadow-none"
              style={{ height: PDF_TEMPLATE_BODY_MIN_HEIGHT }}
            />
            {errors.bodyContent ? (
              <p className="mt-1 text-[11px] font-medium text-[#c74b40]">{errors.bodyContent}</p>
            ) : null}
          </div>
        </div>
      </SectionShell>
    </div>
  );
}

export {
  PdfTemplateTab
};
